export interface AuthObject {
  name: string;
  fueCost: number;
}

export interface OptimizationSuggestion {
  description: string;
  currentFueWeight: number;
  currentTierLabel: string;
  optimizedFueWeight: number;
  optimizedTierLabel: string;
}

export interface RoleIntelligenceChildRole {
  roleKey: number;
  roleName: string;
  tierWeight: number;
}

export interface RoleIntelligenceRow {
  id: string;
  roleName: string;
  type: 'Composite' | 'Single';
  currentTier: string;
  costPerUser: number;
  users: number;
  potentialTier: string;
  potentialCostPerUser: number;
  savingsPerUser: number;
  totalSavings: number;
  status: 'Over-licensed' | 'Compliant' | 'Dormant';
  roleDescription: string;
  authObjects: AuthObject[];
  optimization?: OptimizationSuggestion;
  childRoles?: RoleIntelligenceChildRole[];
}

const ROLE_INTELLIGENCE_ROLES_TABLE_MOCK_DATA: RoleIntelligenceRow[] = [
  {
    id: '1',
    roleName: 'Z_SD_ORDER_PROCESSOR',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 52,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 39936,
    status: 'Over-licensed',
    roleDescription:
      'Sales order creation, modification and delivery processing',
    authObjects: [
      { name: 'V_VBAK_AAT', fueCost: 0.4 },
      { name: 'V_VBAK_VKO', fueCost: 0.3 },
      { name: 'F_BKPF_BUK', fueCost: 0.3 },
    ],
    optimization: {
      description:
        'Remove F_BKPF_BUK (financial posting) from sales role — move to add-on',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
    childRoles: [
      { roleKey: 3001, roleName: 'Z_SD_ORDER_CREATE', tierWeight: 0.4 },
      { roleKey: 3002, roleName: 'Z_SD_ORDER_MODIFY', tierWeight: 0.3 },
      { roleKey: 3003, roleName: 'Z_SD_DELIVERY_PROCESS', tierWeight: 0.2 },
      { roleKey: 3004, roleName: 'Z_SD_BILLING_POST', tierWeight: 0.3 },
    ],
  },
  {
    id: '2',
    roleName: 'Z_FI_AP_CLERK',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 34,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 26112,
    status: 'Over-licensed',
    roleDescription:
      'Accounts payable invoice processing and vendor payment management',
    authObjects: [
      { name: 'F_BKPF_BUK', fueCost: 0.5 },
      { name: 'F_KNA1_BUK', fueCost: 0.3 },
      { name: 'F_LFA1_BUK', fueCost: 0.2 },
    ],
    optimization: {
      description:
        'Remove advanced FI authorization objects — core invoice viewing is sufficient',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '3',
    roleName: 'Z_MM_BUYER_ROLE',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 28,
    potentialTier: 'Advanced (1)',
    potentialCostPerUser: 960,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Procurement buyer role for purchase order creation and vendor management',
    authObjects: [
      { name: 'M_BEST_BSA', fueCost: 1.0 },
      { name: 'M_BEST_EKO', fueCost: 0.8 },
      { name: 'M_MATE_WRK', fueCost: 0.5 },
    ],
    childRoles: [
      { roleKey: 3005, roleName: 'Z_MM_PO_CREATE', tierWeight: 0.5 },
      { roleKey: 3006, roleName: 'Z_MM_PO_APPROVE', tierWeight: 0.8 },
      { roleKey: 3007, roleName: 'Z_MM_VENDOR_EVAL', tierWeight: 0.3 },
    ],
  },
  {
    id: '4',
    roleName: 'Z_HR_EMPLOYEE_SELF',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 215,
    potentialTier: 'Self-Service (0.033)',
    potentialCostPerUser: 32,
    savingsPerUser: 160,
    totalSavings: 34400,
    status: 'Dormant',
    roleDescription:
      'Employee self-service for leave requests and personal data updates',
    authObjects: [
      { name: 'P_ORGIN_XX', fueCost: 0.2 },
      { name: 'P_PERNR', fueCost: 0.033 },
    ],
    optimization: {
      description:
        'Self-service workflows only — downgrade to Self-Service tier',
      currentFueWeight: 0.2,
      currentTierLabel: 'Core',
      optimizedFueWeight: 0.033,
      optimizedTierLabel: 'Self-Service',
    },
  },
  {
    id: '5',
    roleName: 'Z_CO_COST_CENTER_CTRL',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 19,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 14592,
    status: 'Over-licensed',
    roleDescription:
      'Cost center controlling with budget monitoring and variance reporting',
    authObjects: [
      { name: 'K_CSKS', fueCost: 0.5 },
      { name: 'K_CSLA', fueCost: 0.3 },
      { name: 'F_BKPF_BUK', fueCost: 0.3 },
    ],
    optimization: {
      description:
        'Remove direct FI posting access — retain read-only CO reporting',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '6',
    roleName: 'Z_SD_SHIPPING_CLERK',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 41,
    potentialTier: 'Advanced (1)',
    potentialCostPerUser: 960,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Shipping and logistics clerk for delivery processing and goods issue',
    authObjects: [
      { name: 'V_LIKP_VST', fueCost: 1.0 },
      { name: 'V_VBAK_AAT', fueCost: 0.8 },
      { name: 'M_MSEG_BWA', fueCost: 0.5 },
    ],
  },
  {
    id: '7',
    roleName: 'Z_FI_ASSET_MANAGER',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 12,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 9216,
    status: 'Dormant',
    roleDescription:
      'Fixed asset management including depreciation and capitalization',
    authObjects: [
      { name: 'A_ANLKL', fueCost: 0.4 },
      { name: 'F_BKPF_BUK', fueCost: 0.3 },
    ],
    optimization: {
      description:
        'Asset viewing access sufficient — remove write-back FI authorization',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '8',
    roleName: 'Z_MM_INVENTORY_USER',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 87,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Inventory management for stock monitoring and goods movement tracking',
    authObjects: [
      { name: 'M_MSEG_BWA', fueCost: 0.2 },
      { name: 'M_MATE_WRK', fueCost: 0.2 },
    ],
  },
  {
    id: '9',
    roleName: 'Z_SD_BILLING_CLERK',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 23,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 17664,
    status: 'Over-licensed',
    roleDescription:
      'Customer billing and invoice generation with revenue account posting',
    authObjects: [
      { name: 'V_VBRK_FKA', fueCost: 0.5 },
      { name: 'F_BKPF_BUK', fueCost: 0.5 },
      { name: 'V_VBAK_AAT', fueCost: 0.2 },
    ],
    optimization: {
      description:
        'Separate revenue posting into dedicated FI role — billing clerk stays Core',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '10',
    roleName: 'Z_HR_TIME_ADMIN',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 9,
    potentialTier: 'Self-Service (0.033)',
    potentialCostPerUser: 32,
    savingsPerUser: 160,
    totalSavings: 1440,
    status: 'Dormant',
    roleDescription:
      'Time and attendance administration for timesheet approval and absence management',
    authObjects: [
      { name: 'P_ORGIN_XX', fueCost: 0.2 },
      { name: 'PT_MSTR_TM', fueCost: 0.033 },
    ],
    optimization: {
      description: 'Timesheet approval only requires Self-Service tier access',
      currentFueWeight: 0.2,
      currentTierLabel: 'Core',
      optimizedFueWeight: 0.033,
      optimizedTierLabel: 'Self-Service',
    },
  },
  {
    id: '11',
    roleName: 'Z_PP_PRODUCTION_PLANNER',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 33,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 25344,
    status: 'Over-licensed',
    roleDescription:
      'Production planning and MRP execution for manufacturing orders',
    authObjects: [
      { name: 'C_AFKO_AWK', fueCost: 0.5 },
      { name: 'F_BKPF_BUK', fueCost: 0.5 },
    ],
    optimization: {
      description: 'Remove direct FI posting — PP planner stays Core',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '12',
    roleName: 'Z_QM_INSPECTOR',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 18,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Quality inspection and defect recording for goods receipt',
    authObjects: [
      { name: 'Q_QMEL', fueCost: 0.2 },
      { name: 'M_MSEG_BWA', fueCost: 0.2 },
    ],
  },
  {
    id: '13',
    roleName: 'Z_FI_GL_ACCOUNTANT',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 45,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 34560,
    status: 'Dormant',
    roleDescription:
      'General ledger accounting with journal entry posting and period close',
    authObjects: [
      { name: 'F_BKPF_BUK', fueCost: 0.8 },
      { name: 'F_BKPF_KOA', fueCost: 0.2 },
    ],
    optimization: {
      description: 'Limit to period close transactions — remove ad-hoc posting',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '14',
    roleName: 'Z_SD_CREDIT_MANAGER',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 11,
    potentialTier: 'Advanced (1)',
    potentialCostPerUser: 960,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Customer credit limit management and blocked order release',
    authObjects: [
      { name: 'F_KNA1_BUK', fueCost: 1.0 },
      { name: 'V_VBAK_AAT', fueCost: 0.8 },
    ],
  },
  {
    id: '15',
    roleName: 'Z_MM_VENDOR_ADMIN',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 7,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 5376,
    status: 'Over-licensed',
    roleDescription: 'Vendor master data creation and bank account maintenance',
    authObjects: [
      { name: 'F_LFA1_BUK', fueCost: 0.6 },
      { name: 'F_LFA1_GEN', fueCost: 0.4 },
    ],
    optimization: {
      description: 'Vendor creation only — remove bank account write access',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '16',
    roleName: 'Z_HR_PAYROLL_ADMIN',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 14,
    potentialTier: 'Advanced (1)',
    potentialCostPerUser: 960,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Payroll run execution and off-cycle payments with wage type configuration',
    authObjects: [
      { name: 'P_PERNR', fueCost: 1.0 },
      { name: 'P_ORGIN_XX', fueCost: 0.8 },
    ],
  },
  {
    id: '17',
    roleName: 'Z_CO_PROFITABILITY',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 8,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 6144,
    status: 'Dormant',
    roleDescription:
      'Profitability analysis reporting and planning in CO-PA module',
    authObjects: [
      { name: 'K_KEPM', fueCost: 0.4 },
      { name: 'K_KEPH', fueCost: 0.3 },
    ],
    optimization: {
      description: 'Read-only CO-PA reporting — remove planning write access',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '18',
    roleName: 'Z_PM_MAINTENANCE_TECH',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 62,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Plant maintenance technician for work order confirmation and time entry',
    authObjects: [
      { name: 'I_AUFK', fueCost: 0.2 },
      { name: 'P_PERNR', fueCost: 0.033 },
    ],
  },
  {
    id: '19',
    roleName: 'Z_SD_RETURNS_PROCESSOR',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 16,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 12288,
    status: 'Over-licensed',
    roleDescription:
      'Customer returns processing with credit memo and re-stocking management',
    authObjects: [
      { name: 'V_VBAK_AAT', fueCost: 0.5 },
      { name: 'F_BKPF_BUK', fueCost: 0.5 },
    ],
    optimization: {
      description:
        'Returns logic does not require direct FI write — split roles',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '20',
    roleName: 'Z_BASIS_BACKGROUND_JOB',
    type: 'Single',
    currentTier: 'Self-Service (0.033)',
    costPerUser: 32,
    users: 120,
    potentialTier: 'Self-Service (0.033)',
    potentialCostPerUser: 32,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Background job scheduling and batch process monitoring for BASIS team',
    authObjects: [{ name: 'S_BTCH_ADM', fueCost: 0.033 }],
  },
  {
    id: '21',
    roleName: 'Z_FI_TAX_SPECIALIST',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 6,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 4608,
    status: 'Dormant',
    roleDescription:
      'Tax reporting and VAT return preparation with external audit access',
    authObjects: [
      { name: 'F_BKPF_BUK', fueCost: 0.5 },
      { name: 'F_T001_BUK', fueCost: 0.3 },
    ],
    optimization: {
      description:
        'Tax viewing sufficient for filing — reduce write authorizations',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '22',
    roleName: 'Z_MM_GOODS_RECEIPT',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 74,
    potentialTier: 'Self-Service (0.033)',
    potentialCostPerUser: 32,
    savingsPerUser: 160,
    totalSavings: 11840,
    status: 'Dormant',
    roleDescription:
      'Goods receipt posting against purchase orders in warehouse',
    authObjects: [
      { name: 'M_MSEG_BWA', fueCost: 0.2 },
      { name: 'M_MATE_WRK', fueCost: 0.033 },
    ],
    optimization: {
      description: 'Single transaction GR posting — Self-Service tier adequate',
      currentFueWeight: 0.2,
      currentTierLabel: 'Core',
      optimizedFueWeight: 0.033,
      optimizedTierLabel: 'Self-Service',
    },
  },
  {
    id: '23',
    roleName: 'Z_SD_PRICING_ANALYST',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 9,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 6912,
    status: 'Over-licensed',
    roleDescription:
      'Pricing condition maintenance and discount approval workflows',
    authObjects: [
      { name: 'V_KONH_VKO', fueCost: 0.6 },
      { name: 'F_BKPF_BUK', fueCost: 0.4 },
    ],
    optimization: {
      description:
        'Pricing config does not need FI posting — remove authorization',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '24',
    roleName: 'Z_IS_DISPLAY_ONLY',
    type: 'Single',
    currentTier: 'Self-Service (0.033)',
    costPerUser: 32,
    users: 310,
    potentialTier: 'Self-Service (0.033)',
    potentialCostPerUser: 32,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Read-only display role for information system reporting across modules',
    authObjects: [{ name: 'S_TCODE', fueCost: 0.033 }],
  },
  {
    id: '25',
    roleName: 'Z_HR_RECRUITER',
    type: 'Composite',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 22,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Recruitment workflow for job postings, candidate tracking and offer management',
    authObjects: [
      { name: 'P_ORGIN_XX', fueCost: 0.2 },
      { name: 'P_PERNR', fueCost: 0.1 },
    ],
  },
  {
    id: '26',
    roleName: 'Z_FI_BANKING_USER',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 5,
    potentialTier: 'Advanced (1)',
    potentialCostPerUser: 960,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Bank statement import and electronic payment file release',
    authObjects: [
      { name: 'F_PAYR_BUK', fueCost: 1.0 },
      { name: 'F_BKPF_BUK', fueCost: 0.8 },
    ],
  },
  {
    id: '27',
    roleName: 'Z_MM_INVOICE_VERIFIER',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 38,
    potentialTier: 'Self-Service (0.033)',
    potentialCostPerUser: 32,
    savingsPerUser: 160,
    totalSavings: 6080,
    status: 'Dormant',
    roleDescription:
      'Three-way match invoice verification against PO and goods receipt',
    authObjects: [
      { name: 'M_RECH_LIB', fueCost: 0.2 },
      { name: 'M_MSEG_BWA', fueCost: 0.033 },
    ],
    optimization: {
      description:
        'Invoice verification is single-step — Self-Service tier sufficient',
      currentFueWeight: 0.2,
      currentTierLabel: 'Core',
      optimizedFueWeight: 0.033,
      optimizedTierLabel: 'Self-Service',
    },
  },
  {
    id: '28',
    roleName: 'Z_CO_INTERNAL_ORDERS',
    type: 'Single',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 13,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 9984,
    status: 'Over-licensed',
    roleDescription:
      'Internal order settlement and overhead allocation in Controlling',
    authObjects: [
      { name: 'K_ORDER', fueCost: 0.5 },
      { name: 'F_BKPF_BUK', fueCost: 0.5 },
    ],
    optimization: {
      description:
        'CO settlement requires no direct FI access — separate roles',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '29',
    roleName: 'Z_SD_CONTRACT_MANAGER',
    type: 'Composite',
    currentTier: 'Advanced (1)',
    costPerUser: 960,
    users: 10,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 768,
    totalSavings: 7680,
    status: 'Dormant',
    roleDescription:
      'Customer contract creation and scheduling agreement management',
    authObjects: [
      { name: 'V_VBAK_AAT', fueCost: 0.6 },
      { name: 'V_VBAK_VKO', fueCost: 0.4 },
    ],
    optimization: {
      description:
        'Contract management does not need financial authorization objects',
      currentFueWeight: 1,
      currentTierLabel: 'Advanced',
      optimizedFueWeight: 0.2,
      optimizedTierLabel: 'Core',
    },
  },
  {
    id: '30',
    roleName: 'Z_PP_CAPACITY_PLANNER',
    type: 'Single',
    currentTier: 'Core (0.2)',
    costPerUser: 192,
    users: 7,
    potentialTier: 'Core (0.2)',
    potentialCostPerUser: 192,
    savingsPerUser: 0,
    totalSavings: 0,
    status: 'Compliant',
    roleDescription:
      'Capacity planning and work center load balancing in PP module',
    authObjects: [
      { name: 'C_AFKO_AWK', fueCost: 0.2 },
      { name: 'C_AFVC_AWK', fueCost: 0.2 },
    ],
  },
];

export default ROLE_INTELLIGENCE_ROLES_TABLE_MOCK_DATA;
